import { useState } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import type { plans } from '@/lib/client';
import { takeMeThere } from '@/stores/teleport';

/** The way to a step left to undo by hand (D13, D14): the settings map's entry, walked in the user's browser. */
function UndoByHand({ plan, step }: { plan: plans.PlanView; step: plans.RevertOutcome }) {
  const [walking, setWalking] = useState(false);
  const [line, setLine] = useState<string | null>(null);
  const title = plan.steps.find((s) => s.key === step.step_key)?.title ?? step.step_key;
  const go = async () => {
    setWalking(true);
    try {
      setLine(await takeMeThere({ entryId: step.settings_map_entry_id!, label: title, from: { planId: plan.id, stepKey: step.step_key } }));
    } finally {
      setWalking(false);
    }
  };
  return (
    <li>
      <div>{step.message}</div>
      {step.instructions && <div className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{step.instructions}</div>}
      {step.settings_map_entry_id && (
        <button onClick={() => void go()} disabled={walking} className="mt-1 inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs text-foreground hover:bg-secondary disabled:opacity-50">
          {walking ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />} Take me there
        </button>
      )}
      {line && <div className="mt-0.5 text-xs text-muted-foreground">{line}</div>}
    </li>
  );
}

/** What the revert undid and what is left to undo by hand (D14). */
export function RevertOutcome({ plan }: { plan: plans.PlanView }) {
  const revert = plan.revert;
  if (!revert) return null;
  const reverted = revert.steps.filter((s) => s.outcome === 'reverted');
  const left = revert.steps.filter((s) => s.outcome !== 'reverted');
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Reverted</h4>
        {reverted.length === 0 ? <p className="mt-1 text-sm text-muted-foreground">Nothing yet.</p> : (
          <ul className="mt-1 space-y-1 text-sm text-foreground">{reverted.map((s) => <li key={s.step_key}>{s.message}</li>)}</ul>
        )}
      </div>
      <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-orange-400">{revert.status === 'reverting' ? 'In progress' : 'Undo by hand'}</h4>
        {left.length === 0 ? <p className="mt-1 text-sm text-muted-foreground">{revert.status === 'reverting' ? 'Reverting…' : 'Nothing left.'}</p> : (
          <ul className="mt-1 space-y-2 text-sm text-foreground">{left.map((s) => <UndoByHand key={s.step_key} plan={plan} step={s} />)}</ul>
        )}
      </div>
    </div>
  );
}
